import { useState } from 'react';

import {
  Box,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon, SettingsIcon } from 'lucide-react';

import ZoomLevelSlider from '@modules/reports/shared/ZoomLevelSlider';
import { useLocale } from '@shared/hooks/locale';

type MapSettingsProps = {
  includePoiMap: boolean;
  poiMapZoomLevel: number | null;
  onIncludePoiMapChange: (value: boolean) => Promise<void>;
  onPoiMapZoomLevelChange: (value: number) => Promise<void>;
};

function MapSettings({
  includePoiMap,
  poiMapZoomLevel,
  onIncludePoiMapChange,
  onPoiMapZoomLevelChange,
}: MapSettingsProps): React.ReactElement {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);

  const displayedZoomLevel = poiMapZoomLevel ?? 14;

  return (
    <Box
      sx={{
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0.5,
          px: 2,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <SettingsIcon size={20} color={'var(--mui-palette-text-secondary)'} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
            }}
          >
            {t('Settings')}
          </Typography>
          {!expanded && includePoiMap && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              • {t('Map enabled')} • {t('Zoom')}: {displayedZoomLevel}
            </Typography>
          )}
        </Stack>
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? (
            <ChevronUpIcon size={20} />
          ) : (
            <ChevronDownIcon size={20} />
          )}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Stack sx={{ p: 1, gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={includePoiMap}
                onChange={e => onIncludePoiMapChange(e.target.checked)}
              />
            }
            label={t('Display map in report')}
            sx={{ m: 0 }}
          />

          <ZoomLevelSlider
            value={poiMapZoomLevel}
            defaultValue={14}
            onChange={onPoiMapZoomLevelChange}
            disabled={!includePoiMap}
          />
        </Stack>
      </Collapse>
    </Box>
  );
}

export default MapSettings;
